<template>
  <div class="folders-files-pages-grid py-2 h-full">
    <div v-if="!!activeFile" class="h-full">
      <NSpace v-if="activeFile.content_thumbnails?.length" wrap>
        <GridItem
          v-for="(thumbnail, idx) in activeFile.content_thumbnails"
          :key="thumbnail"
          :is-selected="selectedPages.some((page) => page.file.id === activeFile?.id && page.page_index === idx)"
          :item="{
            file: { id: activeFile.id, name: activeFile.name },
            thumbnail_url: thumbnail,
            page_index: idx,
            id: createPageId(activeFile.id, idx),
            type: 'page',
            metadata: activeFile.metadata,
          }"
          @item-clicked="api.fileOpener?.({ fileId: activeFile.id, pageIndex: idx })"
          @item-deselected="deselectPage"
          @item-selected="selectItem"
        />
      </NSpace>
      <div v-else class="h-full w-full flex items-center justify-center" :style="{ color: themeVars.primary3 }">
        <div class="flex flex-col gap-3 items-center justify-center max-w-[400px] text-center">
          <CIcon icon="circle-exclamation" size="36px" />
          <div class="font-size-[20px]">
            {{ t('canvasUI.contentSelectorApp.foldersFiles.thumbnailsAreMissing') }}
          </div>
          <div class="font-size-[14px]">
            {{ t('canvasUI.contentSelectorApp.foldersFiles.pdfReuploadHint') }}
          </div>
        </div>
      </div>
    </div>
    <template v-else>
      <div v-if="!!folders.length" class="folders-grid">
        <div class="font-600 mb-3">{{ t('canvasUI.contentSelectorApp.foldersFilesGrid.folders') }}</div>
        <NSpace wrap>
          <GridItem
            v-for="folder in folders"
            :key="folder.id"
            :item="{ ...folder, type: 'folder' }"
            @itemClicked="onFolderClicked"
          />
        </NSpace>
      </div>
      <div v-if="!!files.length" class="files-grid">
        <div class="font-600 my-3">{{ t('canvasUI.contentSelectorApp.foldersFilesGrid.files') }}</div>
        <NSpace wrap>
          <GridItem
            v-for="file in files"
            :key="file.id"
            :is-selected="selectedFiles.some((f) => f.id === file.id)"
            :item="{ ...file, content_type: file.content_type, type: 'file' }"
            @item-clicked="onItemClicked"
            @item-deselected="deselectFile"
            @item-expanded="onFileExpanded"
            @item-selected="selectItem"
          />
        </NSpace>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, type ToRefs } from 'vue'
import { NSpace } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import GridItem from '@content-selector/components/ui/GridItem.vue'
import { useStore, useStoreApi } from '@content-selector/composables/useStore'
import { createPageId } from '@content-selector/util/file'
import { isModifierClick } from '@lib/util/navigation.util'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { MiniFolder, FolderFile } from '@openapi'
import type { AppProps } from '@content-selector/appTypes'

const themeVars = useThemeVars()
const { t } = useI18n()
const {
  activeFile,
  selectedFiles,
  selectedPages,
  setState,
  selectItem,
  deselectFile,
  deselectPage,
  setSelectedSection,
  displayedFiles,
} = useStore()
const { state: api } = useStoreApi()

const appProps = inject<ToRefs<AppProps>>('appProps')

const filteredDisplayedFiles = computed(() => {
  if (!appProps?.disableNoPptxData?.value) {
    return displayedFiles.value
  }

  return displayedFiles.value.filter((item: any) => {
    // Hide items that don't have pptx_data when disableNoPptxData is true
    if ('metadata' in item && !item?.metadata?.pptx_data) {
      return false
    }
    return true
  })
})

const folders = computed((): any[] => filteredDisplayedFiles.value.filter((item: any) => item.type === 'folder'))
const files = computed((): any[] => filteredDisplayedFiles.value.filter((item: any) => item.type === 'file'))

async function onFolderClicked(folder: MiniFolder) {
  setSelectedSection('all')
  setState({ activeFolder: await api.folderItemsFetcher?.(folder.id), activeFile: null })
}

async function onFileExpanded(file: FolderFile) {
  setState({ activeFile: await api.fileFetcher?.(file.id) })
}

function onItemClicked(item: FolderFile, e: MouseEvent) {
  api.fileOpener?.({ fileId: item.id, newTab: isModifierClick(e) })
}
</script>
